import React, { useEffect, useState, useContext, useCallback } from "react";
import Button from "react-bootstrap/Button";
import { OrgProfile, OrgTypes } from "../store/actions";
import { EmailService } from "../services/Email";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../store";
import { useMsal } from "../utils/authContext";
import AlertDismissible from "../common/AlertDismissible";

const HandleConfirm = ({
  email,
  dispatch,
  token,
  info,
  isVerified,
}: {
  email: EmailService;
  dispatch: React.Dispatch<any>;
  token: string;
  info: OrgProfile;
  isVerified: boolean;
}) => {
  const [localConfirmed, setLocalConfirmed] = useState(false);
  const navigate = useNavigate();
  const confirmEmail = useCallback(() => {
    if (token) {
      return email.confirm(token);
    }
  }, [email, token]);
  useEffect(() => {
    if (!localConfirmed && !isVerified) {
      (async () => {
        try {
          setLocalConfirmed(true);

          dispatch({
            type: OrgTypes.setProfile,
            payload: {
              ...info,
              email_verified: true,
              email_verification_in_progress: false,
            },
          });
          await confirmEmail();
        } catch (err: any) {
          console.error(err);
        }
      })();
    }
  }, [dispatch, navigate, info, localConfirmed, confirmEmail, isVerified]);

  if (localConfirmed) {
    return (
      <AlertDismissible
        variant="primary"
        className="mb-3"
        handleClose={() => navigate("/")}
      >
        Thank you for confirming your account!
      </AlertDismissible>
    );
  }
  return null;
};

const ConfirmEmail = () => {
  const context = useMsal();
  const email = new EmailService(context);
  const { state, dispatch } = useContext(AppContext);
  const info = state.orgInfo;
  const [localResent, setLocalResent] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const verified = urlParams.get("verified");
  const needsVerify = info?.auth_id && !info.loading && !info?.email_verified;
  const inProgress =
    info?.auth_id && !info.loading && info?.email_verification_in_progress;
  const isVerified = info?.auth_id && !info.loading && info?.email_verified;
  const isLoading = info?.auth_id && info.loading;
  const needsConfirm = token && verified && !info.loading;

  const verifyEmail = async () => {
    if (inProgress) {
      setLocalResent(true);
    }
    dispatch({
      type: OrgTypes.setProfile,
      payload: { ...info, email_verification_in_progress: true },
    });
    return email.verify();
  };
  if (isLoading) {
    return null;
  } else {
    if (needsConfirm) {
      return (
        <HandleConfirm
          email={email}
          dispatch={dispatch}
          token={token}
          info={info}
          isVerified={!!isVerified}
        />
      );
    }
    if (isVerified) {
      return null;
    }
    if (inProgress) {
      return (
        <AlertDismissible variant="warning" className="mb-3">
          We sent you an email to confirm your account. Please check your inbox.{" "}
          {!localResent && (
            <Button onClick={verifyEmail} size="sm" variant="primary">
              Resend
            </Button>
          )}
          {localResent && <span>Email re-sent</span>}
        </AlertDismissible>
      );
    }
    if (needsVerify && !inProgress) {
      verifyEmail();
    }
  }
  return null;
};
export default ConfirmEmail;
